import { memo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Mic, Loader2, X } from "lucide-react";
import { useVoiceAssistant } from "@/hooks/useVoiceAssistant";
import aiDoctorAvatar from "@/assets/ai-doctor-avatar.png";

/**
 * Full-screen modal that appears when AI Agent is active
 * This component should be placed once in the app, typically in App.tsx or a layout component
 * It uses the singleton VoiceAssistant context and automatically shows when a call is active
 */
export const AIAgentModal = memo(function AIAgentModal() {
  const { isActive, isConnecting, isCancelling, toggleConversation, assistantState } = useVoiceAssistant();

  if (!isActive && !isConnecting) return null;

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden flex items-center justify-center font-sans duration-500 animate-in fade-in">
      {/* Background Layers */}
      <div className="absolute inset-0 bg-background/80 backdrop-blur-3xl transition-colors duration-500" />
      
      {/* Ambient Gradients - More organic and spread out */}
      <div className="absolute top-[-20%] left-[-10%] w-[800px] h-[800px] bg-primary/20 rounded-full blur-[120px] animate-pulse-soft mix-blend-screen dark:mix-blend-overlay" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] bg-accent/20 rounded-full blur-[100px] animate-pulse-soft delay-1000 mix-blend-screen dark:mix-blend-overlay" />
      
      {/* Cinematic Noise Overlay for Texture */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none mix-blend-overlay" 
        style={{ 
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='1' /%3E%3C/svg%3E")`,
          transform: 'translateZ(0)' // Hardware accelerate
        }} 
      />

      {/* Main Glass Card */}
      <div className="relative z-10 w-full max-w-sm mx-4 perspective-1000">
        <div className="bg-white/40 dark:bg-black/40 backdrop-blur-2xl border border-white/20 dark:border-white/10 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)] rounded-[2.5rem] p-8 overflow-hidden relative animate-slide-up group transition-all duration-300 hover:shadow-[0_8px_32px_0_rgba(31,38,135,0.15)]">
          
          {/* Top Sheen */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent opacity-50" />
          
          {/* Avatar Section */}
          <div className="relative flex justify-center mb-8">
             {/* Organic Ripples */}
             {assistantState === "speaking" && (
              <>
                <div className="absolute inset-0 -m-8 rounded-full border border-accent/20 animate-[ping_3s_cubic-bezier(0,0,0.2,1)_infinite]" />
                <div className="absolute inset-0 -m-4 rounded-full border border-accent/40 animate-[ping_3s_cubic-bezier(0,0,0.2,1)_infinite_1s]" />
              </>
             )}
             
             {/* Avatar Container with Glass Border */}
             <div className="relative w-28 h-28 rounded-full p-1 bg-gradient-to-b from-white/80 to-white/20 shadow-lg ring-1 ring-white/50">
                <div className="w-full h-full rounded-full overflow-hidden bg-muted relative">
                  <img 
                    width={112}
                    height={112}
                    src={aiDoctorAvatar} 
                    alt="Dr. Maya" 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  {/* Overlay for connecting state */}
                  {assistantState === "connecting" && (
                    <div className="absolute inset-0 bg-secondary/80 backdrop-blur-[2px] flex items-center justify-center">
                      <Loader2 className="w-8 h-8 text-white animate-spin" />
                    </div>
                  )}
                </div>
                
                {/* Status Badge */}
                <div className={`absolute -bottom-2 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-[10px] uppercase tracking-wider font-bold shadow-sm backdrop-blur-md border border-white/20 transition-all duration-300 ${
                  assistantState === "connecting" 
                    ? "bg-gray-100/90 text-gray-500" 
                    : assistantState === "speaking" 
                      ? "bg-accent/90 text-white shadow-accent/20" 
                      : "bg-primary/90 text-white shadow-primary/20"
                }`}>
                  {assistantState === "connecting" ? "Connecting" : assistantState === "speaking" ? "Speaking" : "Listening"}
                </div>
             </div>
          </div>

          {/* Text Content */}
          <div className="text-center space-y-3 mb-8">
            <h2 className="font-display text-2xl font-bold tracking-tight text-foreground/90">
              {assistantState === "connecting" 
                ? "Connecting to Dr. Maya..." 
                : "Dr. Maya is here"}
            </h2>
            <p className="text-muted-foreground text-sm font-medium leading-relaxed max-w-[260px] mx-auto">
              {assistantState === "connecting" 
                ? "Establishing a secure connection for your consultation."
                : assistantState === "speaking" 
                  ? "Listening to expert medical advice tailored for you." 
                  : "I'm listening. Ask about treatments, costs, or hospitals."}
            </p>
          </div>

          {/* Dynamic Waveform (Visual only) */}
          <div className="h-12 flex items-center justify-center gap-1.5 mb-8 opacity-80">
            {assistantState === "speaking" ? (
               [...Array(5)].map((_, i) => (
                <div 
                  key={i}
                  className="w-1.5 bg-accent rounded-full animate-[bounce_1s_infinite]"
                  style={{ 
                    height: '20%',
                    animationDelay: `${i * 0.1}s`
                  }} 
                />
               ))
            ) : assistantState === "listening" ? (
               <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
                  <Mic className="w-5 h-5 text-primary" />
               </div>
            ) : (
              <div className="w-16 h-1 rounded-full bg-muted" />
            )}
          </div>

          {/* Action Button */}
          <Button 
            onClick={toggleConversation}
            variant="outline"
            disabled={assistantState === "connecting" && isCancelling} 
            className="w-full h-12 rounded-xl border-destructive/20 bg-destructive/5 hover:bg-destructive/10 text-destructive font-medium tracking-wide transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:pointer-events-none"
          >
            {isCancelling ? (
              <>Cancelling...</>
            ) : assistantState === "connecting" ? (
              <>Cancel Call</>
            ) : (
              <>
                <X className="w-4 h-4 mr-2" />
                End Session
              </>
            )}
          </Button>

          {/* Footer Policy Links */}
          <p className="text-[10px] text-muted-foreground/60 text-center mt-6">
            By using this service, you agree to our <Link to="/privacy-policy/" className="underline hover:text-foreground transition-colors">Privacy Policy</Link> and <Link to="/terms-of-service/" className="underline hover:text-foreground transition-colors">Terms of Service</Link>.
          </p>
        </div>
      </div>
    </div>
  );
});
